import { useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useAuth, useClerk, useUser } from '@clerk/react';
import { useConvexAuth } from 'convex/react';
import { m } from '@/paraglide/messages.js';
import { Spinner } from '@/components/ui/spinner';

type HomeSearch = {
  __clerk_ticket?: string;
  o?: string;
  d?: string;
};

export const Route = createFileRoute('/')({
  validateSearch: (search: Record<string, unknown>): HomeSearch => ({
    __clerk_ticket: (search.__clerk_ticket as string) || undefined,
    o: (search.o as string) || undefined,
    d: (search.d as string) || undefined,
  }),
  component: Home,
});

function Home() {
  const {
    __clerk_ticket: token,
    o: orgSlug,
    d: departmentSlug,
  } = Route.useSearch();
  const { isLoaded, isSignedIn } = useAuth();

  // Show spinner while Clerk initializes (prevents blank page flash)
  if (!isLoaded) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (isSignedIn) {
    return (
      <AuthenticatedRedirect
        orgSlug={orgSlug}
        departmentSlug={departmentSlug}
      />
    );
  }

  if (token) {
    return (
      <TokenSignIn
        token={token}
        orgSlug={orgSlug}
        departmentSlug={departmentSlug}
      />
    );
  }

  return <LinkRequired />;
}

function LinkRequired() {
  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <div className="text-center max-w-sm space-y-4">
        <img
          src="/ovet.svg"
          alt="Ovet"
          className="mx-auto size-24"
          width="416"
          height="388"
        />
        <h1 className="text-xl font-semibold text-gray-900">
          {m.signIn_linkRequiredTitle()}
        </h1>
        <p className="text-gray-600">{m.signIn_linkRequiredDescription()}</p>
      </div>
    </div>
  );
}

type SignInError = 'token_used' | 'generic' | null;

function isClerkTokenUsedError(err: unknown): boolean {
  if (typeof err !== 'object' || err === null) return false;
  // Single ClerkError (new Future API)
  if (
    'code' in err &&
    (err as { code?: string }).code === 'sign_in_token_already_used_code'
  ) {
    return true;
  }
  // Legacy thrown error with errors array
  if (
    'errors' in err &&
    Array.isArray((err as { errors: Array<unknown> }).errors)
  ) {
    return (err as { errors: Array<{ code?: string }> }).errors.some(
      (e) => e.code === 'sign_in_token_already_used_code',
    );
  }
  return false;
}

function TokenSignIn({
  token,
  orgSlug,
  departmentSlug,
}: {
  token: string;
  orgSlug?: string;
  departmentSlug?: string;
}) {
  const [clerkAuthComplete, setClerkAuthComplete] = useState<boolean>(false);
  const [error, setError] = useState<SignInError>(null);
  const clerk = useClerk();
  const { user } = useUser();
  const { isAuthenticated: convexAuthenticated } = useConvexAuth();
  const navigate = useNavigate();

  // Prevent React Strict Mode from running sign-in twice
  const signInStartedRef = useRef(false);

  // Step 1: Authenticate with Clerk
  useEffect(() => {
    if (!token || user || clerkAuthComplete || signInStartedRef.current) {
      return;
    }

    // Mark that we've started the sign-in process
    signInStartedRef.current = true;

    const createSignIn = async () => {
      try {
        const result = await clerk.client.signIn.create({
          strategy: 'ticket',
          ticket: token,
        });

        if (result.status === 'complete') {
          await clerk.setActive({ session: result.createdSessionId });

          // Clean up URL by removing token parameter
          window.history.replaceState(null, '', window.location.pathname);

          // Mark Clerk auth as complete, but don't navigate yet
          setClerkAuthComplete(true);
        } else {
          console.error('Sign-in incomplete:', result.status);
          window.history.replaceState(null, '', window.location.pathname);
          setError('generic');
        }
      } catch (err) {
        window.history.replaceState(null, '', window.location.pathname);
        if (!isClerkTokenUsedError(err)) {
          Sentry.captureException(err);
        }
        setError(isClerkTokenUsedError(err) ? 'token_used' : 'generic');
      }
    };

    createSignIn();
  }, [clerk, token, user, clerkAuthComplete]);

  // Step 2: Wait for Convex to receive the auth token, then navigate
  useEffect(() => {
    if (clerkAuthComplete && convexAuthenticated) {
      navigate({
        to: '/onboarding',
        search: { o: orgSlug, d: departmentSlug },
      });
    }
  }, [
    clerkAuthComplete,
    convexAuthenticated,
    navigate,
    orgSlug,
    departmentSlug,
  ]);

  if (error) {
    return <SignInErrorScreen error={error} />;
  }

  return (
    <div className="flex min-h-screen items-center justify-center">
      <Spinner />
    </div>
  );
}

function SignInErrorScreen({ error }: { error: NonNullable<SignInError> }) {
  const isTokenUsed = error === 'token_used';

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <div className="text-center max-w-sm space-y-4">
        <img
          src="/ovet.svg"
          alt="Ovet"
          className="mx-auto size-16 opacity-50"
          width="416"
          height="388"
        />
        <h1 className="text-xl font-semibold text-gray-900">
          {isTokenUsed ? m.signIn_linkUsedTitle() : m.signIn_errorTitle()}
        </h1>
        <p className="text-gray-600">
          {isTokenUsed
            ? m.signIn_linkUsedDescription()
            : m.signIn_errorDescription()}
        </p>
        <p className="text-sm text-gray-500">{m.signIn_askClinic()}</p>
      </div>
    </div>
  );
}

function AuthenticatedRedirect({
  orgSlug,
  departmentSlug,
}: {
  orgSlug?: string;
  departmentSlug?: string;
}) {
  const navigate = useNavigate();
  const { isAuthenticated: convexAuthenticated } = useConvexAuth();

  // Wait for Convex to receive the auth token before navigating.
  // Without this, the onboarding page flashes a loading skeleton
  // because Convex hasn't synced the JWT yet.
  //
  // Thread `o` / `d` through so an already-authenticated user opening
  // a fresh SMS link still gets their clinic context.
  useEffect(() => {
    if (convexAuthenticated) {
      navigate({
        to: '/onboarding',
        search: { o: orgSlug, d: departmentSlug },
      });
    }
  }, [convexAuthenticated, navigate, orgSlug, departmentSlug]);

  return (
    <div className="flex min-h-screen items-center justify-center">
      <Spinner />
    </div>
  );
}
